import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { Card, CardHeader, CardTitle, CardContent } from '../components/Card';
import { Button } from '../components/Button';
import { NotificationModal } from '../components/NotificationModal';
// MIGRATION FIREBASE: Utilise Firebase direct au lieu de l'API Python
import { notificationsService, customersService } from '../services/firebaseService';
import { useToast } from '../components/Toast';
import { confirmMarketingPreview } from '../components/confirmMarketingPreview';
import { Bell, Plus, Send, Clock, Check, Edit2, Trash2, Calendar, Users, Eye, Target, History, RefreshCw } from 'lucide-react';

export const Notifications = () => {
  const toast = useToast();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [activeTab, setActiveTab] = useState('scheduled');
  const [stats, setStats] = useState({
    total_sent: 0,
    open_rate: 0,
  });

  // Segments - chargés dynamiquement depuis le backend
  const [segments, setSegments] = useState([
    { id: 'all', name: 'Tous les clients', icon: '👥', count: 0, color: 'blue', value: 'all', label: 'Tous les clients' },
    { id: 'new', name: 'Nouveaux clients', icon: '✨', count: 0, color: 'green', value: 'new', label: 'Nouveaux clients (30j)' },
    { id: 'regulars', name: 'Clients réguliers', icon: '⭐', count: 0, color: 'purple', value: 'regulars', label: 'Réguliers (3+ cmd)' },
    { id: 'vip', name: 'Clients VIP', icon: '💎', count: 0, color: 'yellow', value: 'vip', label: 'VIP (500€+)' },
    { id: 'inactive', name: 'Inactifs', icon: '💤', count: 0, color: 'gray', value: 'inactive', label: 'Inactifs (30j)' },
  ]);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [withPushToken, setWithPushToken] = useState(0);
  const [loadingSegments, setLoadingSegments] = useState(false);
  // Estimations calculées par le serveur, comme à l'envoi (null = indisponible).
  const [audience, setAudience] = useState({ marketing: null, service: null });

  useEffect(() => {
    loadNotifications();
    loadSegments();
    loadAudience();
  }, []);

  const loadAudience = async () => {
    const estimate = (category) => notificationsService.previewAudience(category)
      .then((preview) => preview.estimated_recipients).catch(() => null);
    const [marketing, service] = await Promise.all([estimate('marketing'), estimate('service')]);
    setAudience({ marketing, service });
  };

  const loadSegments = async () => {
    try {
      setLoadingSegments(true);
      // FIREBASE DIRECT
      const data = await customersService.getSegmentsStats();

      console.log('📊 Segments chargés:', data);

      if (data.success && data.segments) {
        setSegments([
          { id: 'all', name: 'Tous les clients', icon: '👥', count: data.segments.all || 0, color: 'blue', value: 'all', label: 'Tous les clients' },
          { id: 'new', name: 'Nouveaux clients', icon: '✨', count: data.segments.new || 0, color: 'green', value: 'new', label: 'Nouveaux (30j)' },
          { id: 'regulars', name: 'Clients réguliers', icon: '⭐', count: data.segments.regulars || 0, color: 'purple', value: 'regulars', label: 'Réguliers (3+ cmd)' },
          { id: 'vip', name: 'Clients VIP', icon: '💎', count: data.segments.vip || 0, color: 'yellow', value: 'vip', label: 'VIP (500€+)' },
          { id: 'inactive', name: 'Inactifs', icon: '💤', count: data.segments.inactive || 0, color: 'gray', value: 'inactive', label: 'Inactifs (30j)' },
        ]);
        setTotalCustomers(data.total_customers || 0);
        setWithPushToken(data.with_push_token || 0);
      }
    } catch (error) {
      console.error('Erreur chargement segments:', error);
    } finally {
      setLoadingSegments(false);
    }
  };

  const loadNotifications = async () => {
    try {
      setLoading(true);
      // FIREBASE DIRECT
      const result = await notificationsService.getAll();
      const allNotifications = result.notifications || [];

      console.log('📋 Notifications chargées:', allNotifications);
      setNotifications(allNotifications);
      
      // Calculer les stats
      const sent = allNotifications.filter(n => n.status === 'sent').length;
      setStats({
        total_sent: sent,
        open_rate: sent > 0 ? 77 : 0,
      });
    } catch (error) {
      console.error('Failed to load notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (id, category = 'marketing') => {
    try {
      // Aperçu serveur exact, puis accord distinct sur cet aperçu.
      const result = await notificationsService.proposeAndSend(id, confirmMarketingPreview, category);
      if (result.cancelled) { toast.info('Rien envoyé', result.message); return; }
      console.log('📤 Réponse envoi:', result);
      if (result.success) toast.success('Notification acceptée !', result.message);
      else toast.warning('Envoi à vérifier', result.message);

      // Recharger les notifications pour mettre à jour l'affichage
      await loadNotifications();

      // Basculer vers l'onglet Récentes pour voir la notif envoyée
      setActiveTab(result.success ? 'recent' : 'review');
    } catch (error) {
      console.error('Erreur envoi:', error);
      toast.error('Erreur d\'envoi', error.response?.data?.detail || error.message);
      await loadNotifications();
      setActiveTab('review');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette notification ?')) return;

    try {
      // FIREBASE DIRECT
      await notificationsService.delete(id);
      await loadNotifications();
      toast.success('Supprimée !', 'La notification a été supprimée');
    } catch (error) {
      toast.error('Erreur', 'Impossible de supprimer la notification');
    }
  };

  // Filtrer les notifications par STATUS
  const drafts = notifications.filter(n => n.status === 'draft');
  const review = notifications.filter(n => ['sending', 'delivery_unknown', 'partial', 'failed'].includes(n.status));
  const scheduled = notifications.filter(n => n.status === 'scheduled');
  const sent = notifications.filter(n => n.status === 'sent');
  
  // Récentes = envoyées dans les 48h
  const twoDaysAgo = new Date();
  twoDaysAgo.setHours(twoDaysAgo.getHours() - 48);
  const recent = sent.filter(n => {
    const sentAt = n.sent_at ? new Date(n.sent_at) : null;
    return sentAt && sentAt > twoDaysAgo;
  });
  
  // Historique = toutes les envoyées (max 50)
  const history = sent
    .sort((a, b) => new Date(b.sent_at || b.created_at) - new Date(a.sent_at || a.created_at))
    .slice(0, 50);

  // Notifications à afficher selon l'onglet actif
  let displayedNotifications = [];
  if (activeTab === 'draft') {
    displayedNotifications = drafts;
  } else if (activeTab === 'review') {
    displayedNotifications = review;
  } else if (activeTab === 'scheduled') {
    displayedNotifications = scheduled;
  } else if (activeTab === 'recent') {
    displayedNotifications = recent;
  } else if (activeTab === 'history') {
    displayedNotifications = history;
  }

  const tabs = [
    { id: 'draft', label: 'Brouillons', icon: Edit2, count: drafts.length },
    { id: 'review', label: 'À vérifier', icon: Eye, count: review.length },
    { id: 'scheduled', label: 'Planifiées', icon: Clock, count: scheduled.length },
    { id: 'recent', label: 'Récentes (48h)', icon: Check, count: recent.length },
    { id: 'history', label: 'Historique', icon: History, count: history.length }
  ];

  if (loading) {
    return (
      <div>
        <Header title="🔔 Notifications" subtitle="Communiquez avec vos clients" />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header title="🔔 Notifications" subtitle="Communiquez avec vos clients" />
      
      <div className="p-6 space-y-6">
        {/* Statistiques globales */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total envoyées</p>
                  <p className="text-3xl font-bold text-gray-800">{stats.total_sent}</p>
                </div>
                <Send className="w-10 h-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Taux d'ouverture</p>
                  <p className="text-3xl font-bold text-green-600">{stats.open_rate}%</p>
                </div>
                <Eye className="w-10 h-10 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Planifiées</p>
                  <p className="text-3xl font-bold text-orange-600">{scheduled.length}</p>
                </div>
                <Clock className="w-10 h-10 text-orange-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Clients</p>
                  <p className="text-3xl font-bold text-purple-600">{totalCustomers}</p>
                  {withPushToken > 0 && (
                    <p className="text-xs text-green-600">{withPushToken} avec notifs</p>
                  )}
                </div>
                <Users className="w-10 h-10 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Audience par type de notification */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Promotion (clients consentants)</p>
              <p className="text-3xl font-bold text-gray-800">{audience.marketing ?? '—'}</p>
              <p className="text-xs text-gray-500">appareils ayant accepté les offres</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Message de service (tout le monde)</p>
              <p className="text-3xl font-bold text-gray-800">{audience.service ?? '—'}</p>
              <p className="text-xs text-gray-500">Envoyé à tous les appareils ; aucune promotion autorisée</p>
            </CardContent>
          </Card>
        </div>

        {/* Segments de clients */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
            <div className="flex items-center justify-between w-full">
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5" />
                Segments de clients
              </CardTitle>
              <Button
                size="sm"
                variant="outline"
                onClick={loadSegments}
                disabled={loadingSegments}
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${loadingSegments ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {segments.map((segment) => (
                <button
                  key={segment.id}
                  onClick={() => {
                    setEditingNotification({ target_segment: segment.id });
                    setShowModal(true);
                  }}
                  className="p-4 border-2 rounded-lg hover:border-primary transition-all text-left hover:shadow-md"
                >
                  <div className="text-3xl mb-2">{segment.icon}</div>
                  <div className="font-bold text-gray-800">{segment.name}</div>
                  <div className="text-sm text-gray-600">{segment.count} clients</div>
                  <Button size="sm" className="w-full mt-3">
                    <Send className="w-3 h-3 mr-1" />
                    Envoyer
                  </Button>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Onglets Planifiées / Récentes / Historique */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <div className="flex gap-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold transition-all ${
                      activeTab === tab.id 
                        ? 'bg-primary text-white shadow-lg' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      activeTab === tab.id ? 'bg-white/20' : 'bg-gray-200'
                    }`}>
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>
            
            <Button onClick={() => {
              setEditingNotification(null);
              setShowModal(true);
            }}>
              <Plus className="w-4 h-4 mr-2" />
              Créer une notification
            </Button>
          </div>

          {/* Liste des notifications */}
          {displayedNotifications.length === 0 ? (
            <Card className="text-center py-12">
              <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-semibold mb-2">
                {activeTab === 'draft' && 'Aucun brouillon'}
                {activeTab === 'review' && 'Aucun envoi à vérifier'}
                {activeTab === 'scheduled' && 'Aucune notification planifiée'}
                {activeTab === 'recent' && 'Aucune notification récente'}
                {activeTab === 'history' && 'Aucune notification dans l\'historique'}
              </p>
              <p className="text-sm text-gray-400 mb-4">
                {activeTab === 'scheduled' && 'Créez une notification et planifiez son envoi'}
                {activeTab === 'recent' && 'Les notifications envoyées dans les dernières 48h apparaîtront ici'}
                {activeTab === 'history' && 'Les 50 dernières notifications envoyées apparaîtront ici'}
              </p>
              {activeTab === 'scheduled' && (
                <Button onClick={() => { setEditingNotification(null); setShowModal(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Créer une notification
                </Button>
              )}
            </Card>
          ) : (
            <div className="space-y-4">
              {displayedNotifications.map((notif) => {
                const isScheduled = notif.status === 'scheduled';
                const isDraft = notif.status === 'draft';
                const needsReview = ['sending', 'delivery_unknown', 'partial', 'failed'].includes(notif.status);
                const scheduledDate = notif.scheduled_at ? new Date(notif.scheduled_at) : null;
                const sentDate = notif.sent_at ? new Date(notif.sent_at) : null;
                const segment = segments.find(s => s.id === notif.target || s.id === notif.target_segment) || segments[0];
                
                return (
                  <Card key={notif.id}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-3xl">{notif.icon || '🔔'}</span>
                          <div>
                            <h4 className="font-bold text-lg">{notif.title}</h4>
                            <p className="text-sm text-gray-500">
                              Créée le {notif.created_at && new Date(notif.created_at).toLocaleString('fr-FR')}
                            </p>
                          </div>
                        </div>
                        
                        <p className="text-sm text-gray-700 mb-3 ml-12">{notif.message}</p>
                        
                        <div className="flex items-center gap-2 flex-wrap ml-12">
                          {isScheduled ? (
                            <span className="text-xs px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 font-semibold flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Programmée: {scheduledDate?.toLocaleString('fr-FR')}
                            </span>
                          ) : isDraft || needsReview ? (
                            <span className="text-xs px-3 py-1.5 rounded-full bg-orange-100 text-orange-800 font-semibold">
                              {isDraft ? 'Brouillon — non envoyé' : {
                                sending: 'En cours — ne pas renvoyer',
                                delivery_unknown: 'Résultat incertain — contrôle manuel requis',
                                partial: 'Envoi partiel — contrôle manuel requis',
                                failed: 'Échec — contrôle manuel requis'
                              }[notif.status]}
                            </span>
                          ) : (
                            <span className="text-xs px-3 py-1.5 rounded-full bg-green-100 text-green-700 font-semibold flex items-center gap-1">
                              <Check className="w-3 h-3" />
                              Envoyée le {sentDate?.toLocaleString('fr-FR')}
                            </span>
                          )}
                          
                          <span className="text-xs px-3 py-1.5 rounded-full font-semibold flex items-center gap-1 bg-gray-100 text-gray-700">
                            {notif.category === 'service' ? '🛠️ Message de service — tous les appareils' : `${segment.icon} ${segment.name}`}
                          </span>

                          {notif.sent_count > 0 && (
                            <span className="text-xs px-3 py-1.5 rounded-full bg-purple-100 text-purple-700 font-semibold flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {notif.sent_count} envois
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        {(isScheduled || isDraft) && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => {
                              setEditingNotification(notif);
                              setShowModal(true);
                            }} title="Modifier">
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button size="sm" onClick={() => handleSend(notif.id, notif.category === 'service' ? 'service' : 'marketing')} title="Envoyer maintenant">
                              <Send className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="danger" disabled={['sending', 'delivery_unknown'].includes(notif.status)} onClick={() => handleDelete(notif.id)} title="Supprimer">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      <NotificationModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingNotification(null);
        }}
        notification={editingNotification}
        segments={segments}
        audience={audience}
        onSuccess={() => { loadNotifications(); loadAudience(); }}
      />
    </div>
  );
};
